import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";

type CibilScoreResponse = {
  success: boolean;
  score?: number;
  message?: string;
};

export const CibilScoreCheck = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [result, setResult] = useState<CibilScoreResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    fullName: "",
    panNumber: "",
    dateOfBirth: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasConsent) {
      setError("Please provide consent to check your CIBIL score");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("https://api.example.com/check-cibil", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.fullName,
          pan: formData.panNumber,
          dob: formData.dateOfBirth
        }),
      });

      const data: CibilScoreResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch CIBIL score");
      }

      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      panNumber: "",
      dateOfBirth: ""
    });
    setHasConsent(false);
    setResult(null);
    setError(null);
  };

  const getScoreColor = (score: number) => {
    if (score < 600) return "bg-red-500";
    if (score < 700) return "bg-orange-500";
    if (score < 750) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getScoreLabel = (score: number) => {
    if (score < 600) return "Needs Work";
    if (score < 700) return "Fair";
    if (score < 750) return "Good";
    return "Excellent";
  };

  return (
    <>
      <Button
        variant="outline"
        size="lg"
        className="border-2 border-purple-500 text-purple-400 hover:bg-purple-500/10 text-lg px-8 py-4 h-auto"
        onClick={() => {
          resetForm();
          setIsOpen(true);
        }}
      >
        <span className="mr-2">🔍</span>
        Check Your CIBIL Score
      </Button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-blue-600 mb-2">
                  {result ? "Your CIBIL Score" : "Free CIBIL Score Check"}
                </h2>
                <p className="text-gray-600">
                  {result ? "Here's your credit score" : "Get your free credit score report instantly"}
                </p>
              </div>

              {!result ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="panNumber">PAN Number</Label>
                    <Input
                      id="panNumber"
                      type="text"
                      placeholder="Enter your PAN number"
                      value={formData.panNumber}
                      onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
                      required
                      className="mt-2 uppercase"
                      maxLength={10}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full justify-start text-left font-normal border border-gray-200",
                            !formData.dateOfBirth && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.dateOfBirth ? (
                            format(new Date(formData.dateOfBirth), "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined}
                          onSelect={(date) => {
                            setFormData({ ...formData, dateOfBirth: date?.toISOString() || "" });
                          }}
                          fromYear={1900}
                          toDate={new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="flex items-start space-x-2 pt-2">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={hasConsent}
                      onChange={(e) => setHasConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      required
                    />
                    <label htmlFor="consent" className="text-sm text-gray-600">
                      I authorize the platform to fetch my credit score from credit bureaus
                    </label>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm flex items-center">
                      <XCircle className="h-4 w-4 mr-2" />
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsOpen(false)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-700 text-white min-w-[120px]"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Check Score"
                      )}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-gray-900 mb-2">
                      {result.score}
                    </div>
                    <div className={`text-sm font-medium ${getScoreColor(result.score as number).replace('bg-', 'text-')}`}>
                      {getScoreLabel(result.score as number)}
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getScoreColor(result.score as number)}`}
                        style={{ width: `${((result.score as number - 300) / 500) * 100}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>300</span>
                      <span>900</span>
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="flex items-center justify-center space-x-2 text-green-600">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Successfully retrieved your CIBIL score</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-4 text-center">
                      Your credit score is looking {getScoreLabel(result.score as number).toLowerCase()}!
                    </p>
                  </div>

                  <div className="text-center">
                    <Button
                      onClick={() => setIsOpen(false)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500 text-center mt-6">
                Note: We do not store your credit score. Your data is transmitted securely using encryption protocols.
              </p>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};
